import React, { useEffect, useState } from "react";
import apiClient from "../../../services/apiClient";
import { getHeaders } from "../../ApiHeaders/apiHeaders";
import { datadogRum } from "@datadog/browser-rum";
import Dropdown from "../DropDown";

interface CompanyData {
  company_id: number;
  company_code: string;
  company_name: string;
}

interface CompanyDropdownProps {
  selectedCompany: string;
  onCompanyChange: (selectedCompany: string, companyId: number) => void;
  disabled: boolean;
  className?: string;
  onFocus?: () => void;
}

const CompanyDropdown: React.FC<CompanyDropdownProps> = ({
  selectedCompany,
  onCompanyChange,
  disabled,
  className = "",
  onFocus,
}) => {
  const [companyData, setCompanyData] = useState<CompanyData[]>([]);

  useEffect(() => {
    const fetchCompanyData = async () => {
      const startTime = performance.now();

      try {
        const headers = await getHeaders();
        const response = await apiClient.get("/company/", { headers });

        if (
          response.data.response &&
          Array.isArray(response.data.response.data)
        ) {
          setCompanyData(response.data.response.data);

          datadogRum.addAction("Fetched Companies Successfully", {
            companyCount: response.data.response.data.length,
          });
        } else {
          throw new Error("Invalid response format");
        }
      } catch (error) {
        console.error("Error fetching company data:", error);
        datadogRum.addError("Failed to fetch companies", { error });
      } finally {
        const endTime = performance.now();
        datadogRum.addAction("Company API Response Time", {
          duration: endTime - startTime,
        });
      }
    };

    fetchCompanyData();
  }, []);

  const options = companyData.map((company) => ({
    value: company.company_name,
    label: company.company_name,
    id: company.company_id, // optional, in case you want to use it elsewhere
  }));

  const handleChange = (value: string) => {
    const selected = companyData.find((c) => c.company_name === value);
    onCompanyChange(value, selected?.company_id ?? 0);
  };

  return (
    <Dropdown
      options={options}
      selectedValue={selectedCompany || undefined}
      onValueChange={handleChange}
      disabled={disabled}
      className={className}
      placeholder="Select Company"
      onFocus={onFocus}
    />
  );
};

export default CompanyDropdown;
